import { Link } from 'react-router-dom'

export default function TermsOfService() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <title>Terms of Service - Start Grounded</title>
      <meta name="description" content="Terms of service for Start Grounded AI trainer subscription" />

      <div className="max-w-4xl mx-auto px-4 py-12 safe-top">
        <Link to="/" className="text-blue-600 hover:text-blue-800 mb-8 inline-block">
          ← Back to Home
        </Link>

        <h1 className="text-4xl font-bold text-gray-900 mb-8">Terms of Service</h1>
        
        <div className="bg-white rounded-lg shadow-lg p-8 space-y-6 text-gray-700">
          <p className="text-sm text-gray-500">Last updated: February 19, 2026</p>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Acceptance of Terms</h2>
            <p className="mb-4">By creating an account and using Start Grounded ("the Service"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, you may not use the Service.</p>
            <p>These Terms constitute a legally binding agreement between you and Start Grounded.</p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Description of Service</h2>
            <p className="mb-4">Start Grounded provides AI-powered personal training services, including:</p>
            <ul className="list-disc ml-6 space-y-2">
              <li>Personalized workout program generation</li>
              <li>Automated workout reminders via SMS and voice calls</li>
              <li>Progress tracking and workout history</li>
              <li>Exercise library and instructional content</li>
              <li>AI trainer accountability system</li>
            </ul>
            <p className="mt-4"><strong>Important:</strong> Our service is NOT a substitute for professional medical advice, diagnosis, or treatment. Consult your physician before beginning any exercise program.</p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. Subscription and Billing</h2>
            
            <h3 className="text-xl font-semibold text-gray-800 mb-2">Subscription Plans</h3>
            <p className="mb-4">Start Grounded operates on a premium subscription model. Pricing and plan details are displayed at signup and subject to change with 30 days notice.</p>

            <h3 className="text-xl font-semibold text-gray-800 mb-2 mt-4">Payment Terms</h3>
            <ul className="list-disc ml-6 space-y-2">
              <li>Subscriptions are billed on a recurring basis (monthly or annually)</li>
              <li>Payment is processed automatically using your chosen payment method</li>
              <li>You authorize us to charge your payment method for all subscription fees</li>
              <li>All fees are non-refundable except as required by law</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-2 mt-4">Cancellation</h3>
            <ul className="list-disc ml-6 space-y-2">
              <li>You may cancel your subscription at any time from your account settings</li>
              <li>Cancellation takes effect at the end of your current billing period</li>
              <li>No partial refunds for unused portions of a billing period</li>
              <li>Access to the Service continues until the end of your paid period</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-2 mt-4">Price Changes</h3>
            <p>We reserve the right to modify subscription prices. Existing subscribers will receive 30 days notice before any price increase takes effect.</p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. User Accounts</h2>
            
            <h3 className="text-xl font-semibold text-gray-800 mb-2">Account Creation</h3>
            <ul className="list-disc ml-6 space-y-2 mb-4">
              <li>You must provide a valid phone number to create an account</li>
              <li>You must be at least 18 years old to use the Service</li>
              <li>One account per phone number</li>
              <li>You are responsible for maintaining the security of your account</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-2">Prohibited Activities</h3>
            <p className="mb-2">You may not:</p>
            <ul className="list-disc ml-6 space-y-2">
              <li>Share your account credentials with others</li>
              <li>Use the Service for any illegal purpose</li>
              <li>Attempt to reverse engineer or extract our AI training algorithms</li>
              <li>Upload inappropriate, offensive, or copyrighted content</li>
              <li>Abuse or harass our AI systems or support staff</li>
              <li>Use automated systems to access the Service (bots, scrapers)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Health and Safety</h2>
            
            <h3 className="text-xl font-semibold text-gray-800 mb-2">Medical Disclaimer</h3>
            <p className="mb-4 font-semibold text-red-700">IMPORTANT: Always consult with a qualified healthcare professional before starting any exercise program.</p>
            <p className="mb-4">Start Grounded provides general fitness guidance and is not a medical service. Our AI trainers:</p>
            <ul className="list-disc ml-6 space-y-2">
              <li>Do NOT provide medical advice, diagnosis, or treatment</li>
              <li>Cannot assess individual health conditions or limitations</li>
              <li>Are not a substitute for professional medical evaluation</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-2 mt-4">Assumption of Risk</h3>
            <p className="mb-4">Exercise involves inherent risks including, but not limited to, injury, disability, or death. By using the Service, you acknowledge and accept these risks. You agree to:</p>
            <ul className="list-disc ml-6 space-y-2">
              <li>Exercise within your physical capabilities</li>
              <li>Stop immediately if you experience pain, dizziness, or discomfort</li>
              <li>Use proper form and equipment</li>
              <li>Train in a safe environment</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Intellectual Property</h2>
            
            <h3 className="text-xl font-semibold text-gray-800 mb-2">Our Content</h3>
            <p className="mb-4">All content provided by Start Grounded (workout programs, exercise descriptions, instructional materials, AI-generated content, software, graphics) is owned by us and protected by copyright, trademark, and other intellectual property laws.</p>

            <h3 className="text-xl font-semibold text-gray-800 mb-2">Your Content</h3>
            <p className="mb-4">You retain ownership of content you upload (progress photos, workout notes). By uploading content, you grant us a non-exclusive, worldwide license to use, store, and display that content solely for providing the Service.</p>
            <p>We will never sell or share your personal workout data with third parties for marketing purposes.</p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. SMS and Voice Communication</h2>
            
            <h3 className="text-xl font-semibold text-gray-800 mb-2">Consent to Contact</h3>
            <p className="mb-4">By providing your phone number, you consent to receive:</p>
            <ul className="list-disc ml-6 space-y-2">
              <li>Automated workout reminder SMS messages</li>
              <li>Automated workout reminder voice calls (if enabled)</li>
              <li>Accountability check-in messages</li>
              <li>Service updates and notifications</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-2 mt-4">Opt-Out</h3>
            <p>You may opt out of reminder calls at any time in your account settings. Standard message and data rates may apply.</p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Limitation of Liability</h2>
            <p className="mb-4 font-semibold">TO THE MAXIMUM EXTENT PERMITTED BY LAW:</p>
            <ul className="list-disc ml-6 space-y-2">
              <li>Start Grounded is provided "AS IS" without warranties of any kind</li>
              <li>We are not liable for any injuries, losses, or damages resulting from use of the Service</li>
              <li>Our total liability to you shall not exceed the amount you paid for the Service in the past 12 months</li>
              <li>We are not responsible for interruptions, errors, or technical issues</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Indemnification</h2>
            <p>You agree to indemnify and hold harmless Start Grounded, its employees, and affiliates from any claims, damages, or expenses arising from your use of the Service, violation of these Terms, or violation of any third-party rights.</p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. Termination</h2>
            <p className="mb-4">We reserve the right to suspend or terminate your account at any time for:</p>
            <ul className="list-disc ml-6 space-y-2">
              <li>Violation of these Terms</li>
              <li>Fraudulent payment activity</li>
              <li>Abusive behavior toward our systems or staff</li>
              <li>Any reason at our sole discretion</li>
            </ul>
            <p className="mt-4">Upon termination, your access to the Service will cease immediately. You may still be liable for any unpaid fees.</p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">11. Dispute Resolution</h2>
            
            <h3 className="text-xl font-semibold text-gray-800 mb-2">Governing Law</h3>
            <p className="mb-4">These Terms are governed by the laws of the State of Arizona, United States, without regard to conflict of law principles.</p>

            <h3 className="text-xl font-semibold text-gray-800 mb-2">Arbitration</h3>
            <p className="mb-4">Any disputes arising from these Terms or use of the Service shall be resolved through binding arbitration in accordance with the American Arbitration Association rules, rather than in court.</p>
            
            <h3 className="text-xl font-semibold text-gray-800 mb-2">Class Action Waiver</h3>
            <p>You agree to resolve disputes individually and waive the right to participate in class action lawsuits.</p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">12. Changes to Terms</h2>
            <p className="mb-4">We may modify these Terms at any time. Significant changes will be communicated via:</p>
            <ul className="list-disc ml-6 space-y-2">
              <li>Email notification to your registered account</li>
              <li>SMS notification to your registered phone number</li>
              <li>In-app notification</li>
            </ul>
            <p className="mt-4">Continued use of the Service after changes take effect constitutes acceptance of the updated Terms.</p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">13. Severability</h2>
            <p>If any provision of these Terms is found to be unenforceable, the remaining provisions will continue in full force and effect.</p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">14. Contact Information</h2>
            <p className="mb-2">For questions about these Terms or the Service, contact us:</p>
            <ul className="space-y-1">
              <li><strong>Email:</strong> <a href="mailto:legal@startgrounded.app" className="text-blue-600 hover:underline">legal@startgrounded.app</a></li>
              <li><strong>Support:</strong> <a href="mailto:support@startgrounded.app" className="text-blue-600 hover:underline">support@startgrounded.app</a></li>
            </ul>
          </section>

          <section className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mt-8">
            <h3 className="text-lg font-semibold text-yellow-800 mb-2">Important Health Notice</h3>
            <p className="text-yellow-800">
              Before beginning any exercise program, consult with your physician to ensure it is appropriate for your individual health condition. Stop exercising immediately if you experience pain, dizziness, or unusual discomfort.
            </p>
          </section>
        </div>
      </div>
    </div>
  )
}
